import * as React from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Aperture, Brain, Camera, Globe, Layers, Lightbulb, Move3d, Radio, Scan, UploadCloud, Zap} from 'lucide-react';
import {SERVICES} from '../services';
import {BRAND} from '../brand';

// A brand can feature a subset of services, in its own order (config/brands.yml).
const FEATURED = BRAND.services
    ? BRAND.services.flatMap((slug) => SERVICES.filter((s) => s.slug === slug))
    : SERVICES;

const IconMap: Record<string, React.FC<any>> = {Scan, Camera, Zap, Globe, Layers, Brain, Radio, Lightbulb, Move3d, Aperture};

const Services: React.FC = () => {
    const navigate = useNavigate();
    return (
    <section id="services" className="py-24 bg-lumina-dark relative">
        <div
            className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#0f172a] to-transparent opacity-50 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="mb-16">
                <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                    OUR <span className="text-lumina-accent">SERVICES</span>
                </h2>
                <p className="text-gray-400 max-w-xl text-lg">
                    Bridging the gap between physical reality and digital perfection.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {FEATURED.map((service) => {
                    const Icon = IconMap[service.iconName];
                    const isPrinting = service.interest === 'printing';

                    return (
                        <div
                            key={service.slug}
                            role="link"
                            tabIndex={0}
                            onClick={() => navigate(`/services/${service.slug}`)}
                            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/services/${service.slug}`); }}
                            className="group relative overflow-hidden bg-[#0a0a0f] border border-gray-800 hover:border-lumina-accent hover:shadow-[0_0_30px_color-mix(in_srgb,var(--brand-accent)_15%,transparent)] hover:-translate-y-1 transition-all duration-500 rounded-lg flex flex-col cursor-pointer"
                        >
                            {/* Image. The img starts slightly over-scaled and is GPU-composited so its edge never
                                lands on the container boundary mid-animation (which showed as a hairline seam). */}
                            <Link to={`/services/${service.slug}`} className="block h-48 overflow-hidden relative bg-[#0a0a0f]">
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent z-10"></div>
                                <img
                                    src={service.image}
                                    alt={service.title}
                                    className="w-full h-full object-cover transform-gpu scale-[1.03] group-hover:scale-110 transition-transform duration-700 ease-out opacity-60 group-hover:opacity-80 [backface-visibility:hidden] will-change-transform"
                                />
                                <div
                                    className="absolute top-4 right-4 z-20 bg-black/50 p-2 rounded-full backdrop-blur-sm border border-gray-700 group-hover:bg-lumina-accent/20 group-hover:border-lumina-accent transition-colors duration-300">
                                    <Icon className="w-6 h-6 text-lumina-accent"/>
                                </div>
                            </Link>

                            <div className="p-8 relative z-20 -mt-10 flex-1 flex flex-col">
                                <h3 className="text-xl font-display font-bold text-white mb-3 group-hover:text-lumina-accent transition-colors">
                                    <Link to={`/services/${service.slug}`}>{service.title}</Link>
                                </h3>
                                <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-1">
                                    {service.tagline}
                                </p>

                                <div className="flex items-center gap-4 mt-auto">
                                    <Link to={`/services/${service.slug}`}
                                          className="inline-flex items-center text-xs font-bold text-lumina-purple tracking-widest uppercase hover:text-white transition-colors group/link">
                                        Learn More <span className="ml-2 transform group-hover/link:translate-x-1 transition-transform">→</span>
                                    </Link>
                                    {isPrinting && (
                                        <Link to="/start?interest=printing" onClick={(e) => e.stopPropagation()}
                                              className="inline-flex items-center gap-2 px-4 py-2 bg-lumina-accent/10 border border-lumina-accent/50 text-lumina-accent text-xs font-bold tracking-widest uppercase hover:bg-lumina-accent hover:text-black transition-all rounded">
                                            <UploadCloud className="w-4 h-4"/>
                                            Upload Model
                                        </Link>
                                    )}
                                </div>
                            </div>

                            {/* Hover Glow - Inner Border */}
                            <div
                                className="absolute inset-0 border-2 border-transparent group-hover:border-lumina-accent/20 rounded-lg pointer-events-none transition-all duration-500"></div>
                        </div>
                    );
                })}
            </div>
        </div>
    </section>
    );
};

export default Services;
